import { motion } from "motion/react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../ui/utils";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";
import { DASHBOARD_SIDE_CARD_LIST_HEIGHT_CLASS } from "./dashboardFeedConstants";

interface AlertItem {
  id: string;
  name: string;
  currentStock: number;
  minStock: number;
  daysOnHand: number;
  severity: "critical" | "warning";
}

const MAX_DASHBOARD_ROWS = 5;

function InventoryAlertsEmptyPlaceholder() {
  return (
    <div
      className={`${DASHBOARD_SIDE_CARD_LIST_HEIGHT_CLASS} flex flex-col items-center justify-center px-6 text-center`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-28 h-28 text-muted-foreground/30 mb-3"
        aria-hidden
      >
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <path d="M3.27 6.96L12 12.01l8.73-5.05" />
        <path d="M12 22.08V12" />
      </svg>
      <p className="text-sm text-muted-foreground">No inventory alerts</p>
      <p className="text-xs text-muted-foreground/80 mt-1">Stock levels look good.</p>
    </div>
  );
}

export function InventoryAlerts({
  alertSamples,
}: {
  /** When provided from dashboard API, skip fetching inventory summary. */
  alertSamples?: AlertItem[];
}) {
  const { token } = useAuth();
  const lowStockThreshold = 20;

  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  const severityFor = useMemo(
    () => (currentStock: number): AlertItem["severity"] => {
      if (currentStock <= 0) return "critical";
      return "warning";
    },
    [],
  );

  useEffect(() => {
    if (alertSamples !== undefined) {
      setAlerts(alertSamples.slice(0, MAX_DASHBOARD_ROWS));
      return;
    }
  }, [alertSamples]);

  useEffect(() => {
    if (alertSamples !== undefined) return;
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.reports.getInventorySummary({ token });
        if (cancelled) return;

        const rows = (res.data as any[]) ?? [];
        const lowRows = rows
          .filter((r) => Number(r?.total_stock_qty ?? 0) <= lowStockThreshold)
          .sort((a, b) => Number(a?.total_stock_qty ?? 0) - Number(b?.total_stock_qty ?? 0))
          .slice(0, MAX_DASHBOARD_ROWS);

        setAlerts(
          lowRows.map((r, idx) => {
            const currentStock = Number(r?.total_stock_qty ?? 0);
            return {
              id: String(r?.product_id ?? idx),
              name: String(r?.product_name ?? "Unknown product"),
              currentStock,
              minStock: lowStockThreshold,
              daysOnHand: 0,
              severity: severityFor(currentStock),
            };
          }),
        );
      } catch {
        // ignore - keep empty
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, severityFor, alertSamples]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.3 }}
    >
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <CardTitle>Inventory Alerts</CardTitle>
          </div>
          <Button variant="ghost" size="sm" className="text-primary" asChild>
            <Link
              to="/inventory"
              onClick={() => trackEvent(TRACKING_EVENTS.LOW_STOCK_ALERT_VIEWED, { source: "view_all" })}
            >
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {alerts.length === 0 ? (
            <InventoryAlertsEmptyPlaceholder />
          ) : (
            <div
              className={cn(
                DASHBOARD_SIDE_CARD_LIST_HEIGHT_CLASS,
                "overflow-y-auto space-y-3 pr-1 -mr-1",
              )}
            >
              {alerts.map((alert, index) => (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                  whileHover={{ x: 4 }}
                  className={cn(
                    "p-3 rounded-lg border transition-all cursor-pointer",
                    alert.severity === "critical"
                      ? "bg-red-50 border-red-200"
                      : "bg-amber-50 border-amber-200",
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-foreground">{alert.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          Stock: {alert.currentStock} left
                        </span>
                        <Badge
                          variant={alert.severity === "critical" ? "destructive" : "secondary"}
                          className="text-xs"
                        >
                          {alert.daysOnHand} days left
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs flex-1" asChild>
                      <Link to="/purchase-orders/new">Create Order</Link>
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                      <Link
                        to="/inventory"
                        onClick={() => trackEvent(TRACKING_EVENTS.LOW_STOCK_ALERT_VIEWED, { source: "details" })}
                      >
                        Details
                      </Link>
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
